import axios from 'axios';

import {
  PUBLIC_DUTY_HOUR_TYPES,
  ROSTER_ABSENCE_ROLLOUT,
  ROSTER_REGISTER_ABSENCE,
  PUBLIC_DEFAULT_SETTINGS,
  ROSTER_REGISTER_ABSENCE_DUTIES,
  ROSTER_REGISTER_ABSENCE_PROPOSAL,
  ROSTER_REGISTER_ABSENCE_REASONS
} from 'RestAPIs/endpoints';

export type LeaveRequestDetails = {
  leaveRequestId: number;
  dutyTypeId: number;
  waId: number;
  empNote?: string;
  note?: string;
  employeeName: string;
  employeeId: number;
  employeeNr: number | string;
  valid: {
    from: string;
    to: string;
  };
};

export type RegisterAbsenceReason = {
  code: string;
  id: number;
  name: string;
};

export type DefaultSetting = {
  key: string;
  value: string;
};

export type RegisterAbsenceWorkAgreement = {
  waId: number;
  nr: number;
  weekHoursMin: number;
  benefitId?: number;
  hoursPerWeek?: number;
  fteStartDate?: string | null;
  mondayHours?: boolean;
  tuesdayHours?: boolean;
  wednesdayHours?: boolean;
  thursdayHours?: boolean;
  fridayHours?: boolean;
  saturdayHours?: boolean;
  sundayHours?: boolean;
  functionDescription?: string;
  brName?: string;
  contractKind?: string;
  /** '0' marks a zero-hours contract, whose week hours are entered manually */
  contractCode?: string;
  /** validity of the benefit (inschaling) this row describes */
  validFrom?: string;
  validTo?: string;
};

export type RegisterAbsenceDutiesResponse = {
  workAgreements: RegisterAbsenceWorkAgreement[];
};

export type ProposalDutyPeriod = {
  startTime: string;
  endTime: string;
  dutyTypeId: number;
  dutyHourType?: {
    id: number;
    description: string;
  };
};

export type ProposalDutyLine = {
  id?: number;
  dutyLineId?: number;
  employeeId?: number;
  workAgreementId?: number;
  plannedDutyId?: number;
  costCenterId?: number;
  date: string;
  dutyCode?: string;
  displayDutyCode?: string;
  periods: ProposalDutyPeriod[];
};

export type ProposalOpenRequest = {
  id?: number;
  requestId?: number;
  dutyId?: number;
  dutyCode?: string;
  displayDutyCode?: string;
  functionId?: number;
  costCenterId?: number;
  date?: string;
  periods?: ProposalDutyPeriod[];
};

export type RegisterAbsenceProposalEntry = {
  dutyLine: ProposalDutyLine | null;
  openRequest: ProposalOpenRequest | null;
};

export type CreateAbsenceFromProposalPayload = {
  startDateTime: string;
  endDateTime: string;
  dutyTypeId: number;
  absenceReasonCode: string;
  workAgreementId: number;
  leaveRequestId: number;
  employeeId: number;
  note: string;
  proposals: RegisterAbsenceProposalEntry[];
};

export type PublicDutyHourType = {
  dutyTypeId: number;
  description: string;
  dccSvc: number;
  /** short code of the duty hour type, e.g. 'ZK' (ziekte) */
  code?: string;
  /**
   * flag names of the type; REQ_DISCONNECT_AUTO means detaching a duty gives
   * its underlying request back as an open duty
   */
  flags?: string[];
};

export type AbsenceRolloutWorkAgreementArgument = {
  workAgreementId: number;
  inputDays: string[];
  rolloutStartDate?: string | null;
  inputWeekHours?: number;
};

export type AbsenceRolloutDutyLine = {
  date?: string;
  id?: number;
  dutyLineId?: number;
  /** the underlying request; only a duty with one comes back as an open duty
   *  when it is detached */
  requestId?: number | null;
  waId?: number;
  waNr?: number | string;
  costCenterFullName?: string;
  displayDutyCode?: string;
  dutyCode?: string;
  startTime: string;
  endTime: string;
  totalHours?: number;
  action?: string;
  flags?: string[];
};

export type AbsenceRolloutResponse = {
  existingDutyLines: AbsenceRolloutDutyLine[];
  fteRolloutDutyLines: AbsenceRolloutDutyLine[];
};

export type CreateAbsenceUpdateDutiesPayload = {
  startTime: string;
  endTime: string;
  dutyTypeId: number;
  dccSvc: number;
  note: string;
  /** null keeps the column empty so the note shows as the reason in overviews */
  absenceReasonCode: string | null;
  /** null when the absence is not created from a leave request */
  leaveRequestId: number | null;
  employeeId: number;
  workAgreements: Array<{
    waId: number;
    benefitId?: number;
    hoursWeek: number;
    absPercentage: number;
    dutiesToUpdate: Array<{
      dutyLineId: number;
      dutyDatetimeBegin: string;
      dutyDatetimeEnd: string;
    }>;
    dutiesToDelete: Array<{
      dutyLineId: number;
      dutyDatetimeBegin: string;
      dutyDatetimeEnd: string;
    }>;
    dutiesToCreate: Array<{
      dutyDatetimeBegin: string;
      dutyDatetimeEnd: string;
    }>;
  }>;
};

export const getLeaveRequestDetails = (leaveRequestId: number) =>
  axios.get<LeaveRequestDetails>(
    `secure/users/{username}/leave-requests/${leaveRequestId}`
  );

export const getRegisterAbsenceReasons = () =>
  axios.get<RegisterAbsenceReason[]>(ROSTER_REGISTER_ABSENCE_REASONS);

export const getDefaultSettings = () =>
  axios.get<DefaultSetting[]>(PUBLIC_DEFAULT_SETTINGS);

export const getRegisterAbsenceDutyHourTypes = ({
  startDate,
  endDate
}: {
  startDate: string;
  endDate: string;
}) =>
  axios.get<PublicDutyHourType[]>(PUBLIC_DUTY_HOUR_TYPES, {
    params: {
      group: 'register-absence',
      startDate,
      endDate
    }
  });

export const getRegisterAbsenceDuties = ({
  employeeId,
  startDate,
  endDate
}: {
  employeeId: number;
  startDate: string;
  endDate: string;
}) =>
  axios.get<RegisterAbsenceDutiesResponse>(ROSTER_REGISTER_ABSENCE_DUTIES, {
    params: {
      employeeId,
      startDate,
      endDate
    }
  });

export const getRegisterAbsenceProposal = ({
  employeeId,
  dutyTypeId,
  startDateTime,
  endDateTime,
  workAgreementId
}: {
  employeeId: number;
  dutyTypeId: number;
  startDateTime: string;
  endDateTime: string;
  workAgreementId: number;
}) =>
  axios.get<RegisterAbsenceProposalEntry[]>(ROSTER_REGISTER_ABSENCE_PROPOSAL, {
    params: {
      employeeId,
      dutyTypeId,
      startDateTime,
      endDateTime,
      workAgreementId
    }
  });

export const createAbsenceFromProposal = (
  payload: CreateAbsenceFromProposalPayload
) => axios.post(ROSTER_REGISTER_ABSENCE_PROPOSAL, payload);

export const getAbsenceRollout = (payload: {
  employeeId: number;
  dutyTypeId: number;
  startDate: string;
  endDate: string;
  absencePercentage: number;
  workAgreementArguments: AbsenceRolloutWorkAgreementArgument[];
}) => axios.post<AbsenceRolloutResponse>(ROSTER_ABSENCE_ROLLOUT, payload);

export const createAbsenceUpdateDuties = (
  payload: CreateAbsenceUpdateDutiesPayload
) => axios.post(ROSTER_REGISTER_ABSENCE, payload);

export type RegisteredAbsence = {
  absenceId: number;
  waId: number;
  /** backend datetimes (real UTC), parse with parseBackendDateTime */
  startTime: string;
  endTime: string;
  dutyTypeId: number;
  absPercentage: number;
  hoursWeek: number;
  note?: string | null;
  absenceReasonCode?: string | null;
};

export type AbsenceDetailsResponse = {
  absence: RegisteredAbsence;
  workAgreements: RegisterAbsenceWorkAgreement[];
  /** duty lines linked to the absence */
  duties: AbsenceRolloutDutyLine[];
};

/** everything an edit dialog needs, from only an absence id */
export const getAbsenceById = (absenceId: number) =>
  axios.get<AbsenceDetailsResponse>(ROSTER_REGISTER_ABSENCE, {
    params: { absenceId }
  });

export type UpdateAbsencePayload = {
  absenceId: number;
  /** may only shrink the period: start not before, end not after the original */
  startTime: string;
  endTime: string;
  note: string;
  dutyTypeId: number;
  absenceReasonCode: string | null;
};

// the PUT takes query parameters, not a body - that is how the route reads them
export const updateAbsence = ({ absenceId, ...rest }: UpdateAbsencePayload) =>
  axios.put(ROSTER_REGISTER_ABSENCE, null, {
    params: { absenceId, ...rest }
  });
